import type { CreateKpiInput, DataAttributeDto, DataModelState, DataTypeDto, KpiDto, Role } from '@bk/contracts';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Button, Card, ErrorText, Field, Select } from '../../ui';
import { KpiChart } from './KpiChart';
import { useLevels, visibleIn } from './Livelli';

/**
 * Il costruttore dei grafici (ADR 0213 § 5): si sceglie il dato, cosa misurare, come dividerlo (anche per un campo della
 * voce collegata: le fatture per provincia del cliente), il periodo e il grafico; si vede subito il risultato e si salva
 * nella propria dashboard. Il titolare può darlo a tutti o alle persone di alcuni ruoli. Una misura si può dividere per
 * un'altra, anche di un altro dato (il rapporto: ricavi ÷ camere, scontrini con sconto ÷ scontrini), periodo per periodo.
 */

const isNumber = (a: DataAttributeDto) => a.tipo === 'numero' || a.formato === 'importo' || a.formato === 'numero' || ['numero', 'importo', 'quantita'].includes(a.forma ?? '');
const isDate = (a: DataAttributeDto) => a.tipo === 'data' || ['data', 'data_ora', 'periodo', 'anno'].includes(a.forma ?? '');
const linkOf = (a: DataAttributeDto) => a.verso ?? (a.riferisce && a.riferisce !== 'persona' ? a.riferisce : null);
/** Un campo per cui dividere: un valore breve, non un numero, una data o un testo lungo. */
const isGroup = (a: DataAttributeDto) => a.tipo === 'testo' && !['testo_lungo', 'allegato', 'indirizzo'].includes(a.forma ?? '') && !linkOf(a);

type GroupChoice = '' | 'sede' | 'attivita' | `campo:${string}` | `collegato:${string}`;

export function KpiBuilder({ onSaved, onCancel, forView }: { onSaved?: (k: KpiDto) => void; onCancel: () => void; forView?: { onTile: (titolo: string, domanda: CreateKpiInput) => void } }) {
  const { t, isManager: manager } = useTenant();
  // Nelle viste (ADR 0194 § 2) i tipi e l'anteprima rispettano i permessi del ruolo, e il riquadro va alla vista. Anche
  // nella dashboard di chi vede i dati del suo ruolo (ADR 0232) i tipi sono i soli che legge, e l'anteprima li controlla.
  const isManager = manager && !forView;
  const limited = !manager || !!forView;
  const levels = useLevels();
  const site = levels.site;
  const model = useTenantQuery<DataModelState>(limited ? ['view-types'] : ['data-model'], limited ? '/views/types' : '/data-model');
  const roles = useTenantQuery<{ data: Role[] }>(['roles'], '/roles', { enabled: isManager });
  const types = useMemo(
    () => (model.data?.data ?? []).filter((x) => x.stato === 'attivo' && visibleIn(levels, x.chiave, !!site)).sort((a, b) => a.etichetta.localeCompare(b.etichetta, 'it')),
    [model.data, levels, site],
  );
  const [tipo, setTipo] = useState('');
  const [metrica, setMetrica] = useState<'conteggio' | 'somma' | 'media'>('conteggio');
  const [attributo, setAttributo] = useState('');
  const [data, setData] = useState('');
  const [grana, setGrana] = useState<'mese' | 'anno' | 'giorno'>('mese');
  const [group, setGroup] = useState<GroupChoice>('');
  const [linkedField, setLinkedField] = useState('');
  const [grafico, setGrafico] = useState<'linea' | 'colonne' | 'barre' | 'numero'>('colonne');
  const [name, setName] = useState('');
  const [visibilita, setVisibilita] = useState<'mia' | 'tutti' | 'ruoli'>('mia');
  const [ruoli, setRuoli] = useState<string[]>([]);
  // Il rapporto: la misura divisa per un'altra (ADR 0213 § 3).
  const [ratio, setRatio] = useState(false);
  const [denTipo, setDenTipo] = useState('');
  const [denMetrica, setDenMetrica] = useState<'conteggio' | 'somma' | 'media'>('conteggio');
  const [denAttributo, setDenAttributo] = useState('');
  const [denData, setDenData] = useState('');
  const [percent, setPercent] = useState(false);

  const type = types.find((x) => x.chiave === tipo);
  const attrs = type?.attributi ?? [];
  const numeric = attrs.filter(isNumber);
  const dates = attrs.filter(isDate);
  const links = attrs.filter((a) => linkOf(a) && types.some((x) => x.chiave === linkOf(a)));
  const linkAttr = group.startsWith('collegato:') ? attrs.find((a) => a.nome === group.slice(10)) : undefined;
  const linkedType: DataTypeDto | undefined = linkAttr ? types.find((x) => x.chiave === linkOf(linkAttr)) : undefined;
  const value = attrs.find((a) => a.nome === attributo);
  const denType = ratio ? types.find((x) => x.chiave === denTipo) : undefined;
  const denAttrs = denType?.attributi ?? [];
  const denValue = denAttrs.find((a) => a.nome === denAttributo);
  // Un campo o una voce collegata dividono anche il divisore: si può solo se è dello stesso dato.
  const byValue = group.startsWith('campo:') || group.startsWith('collegato:');
  const groupClash = !!denType && byValue && denType.chiave !== tipo;

  // Un tipo nuovo riparte da capo; il grafico segue la forma (nel tempo colonne, per gruppo barre, altrimenti un numero).
  useEffect(() => {
    setMetrica('conteggio');
    setAttributo('');
    setData('');
    setGroup('');
    setLinkedField('');
  }, [tipo]);
  useEffect(() => {
    setDenMetrica('conteggio');
    setDenAttributo('');
    setDenData('');
  }, [denTipo]);
  // Senza periodo, il divisore non ha la sua data.
  useEffect(() => {
    if (!data) setDenData('');
  }, [data]);
  useEffect(() => setGrafico(group && !data ? 'barre' : data ? 'colonne' : 'numero'), [group, data]);

  const config: CreateKpiInput | null = useMemo(() => {
    if (!type || (metrica !== 'conteggio' && !attributo) || (group.startsWith('collegato:') && !linkedField)) return null;
    if (ratio && (!denType || (denMetrica !== 'conteggio' && !denAttributo) || groupClash)) return null;
    const raggruppa =
      group === 'sede' || group === 'attivita'
        ? { per: group }
        : group.startsWith('campo:')
          ? { per: 'campo' as const, campo: group.slice(6) }
          : linkAttr && linkedType
            ? { per: 'collegato' as const, campo: linkAttr.nome, tipo: linkedType.chiave, campo_collegato: linkedField }
            : null;
    const by =
      group === 'sede'
        ? ' per sede'
        : group === 'attivita'
          ? ' per attività'
          : group.startsWith('campo:')
            ? ` per ${attrs.find((a) => a.nome === group.slice(6))?.etichetta.toLowerCase() ?? ''}`
            : linkAttr && linkedType
              ? ` per ${linkedType.attributi.find((a) => a.nome === linkedField)?.etichetta.toLowerCase() ?? linkedField} di ${linkAttr.etichetta.toLowerCase()}`
              : '';
    const measure = (m: string, v: DataAttributeDto | undefined, field: string, ty: DataTypeDto) => (m === 'conteggio' ? ty.etichetta : `${m === 'somma' ? 'Somma' : 'Media'} di ${v?.etichetta.toLowerCase() ?? field} (${ty.etichetta.toLowerCase()})`);
    const over = ratio && denType ? ` ÷ ${measure(denMetrica, denValue, denAttributo, denType).replace(/^./, (c) => c.toLowerCase())}` : '';
    const what = `${measure(metrica, value, attributo, type)}${over}${by}`;
    return {
      name: name.trim() || what.slice(0, 120),
      tipo: type.chiave,
      metrica,
      attributo: metrica === 'conteggio' ? null : attributo,
      filtri: [],
      attributo_data: data || null,
      grana: data ? grana : null,
      diviso_per:
        ratio && denType
          ? { tipo: denType.chiave, metrica: denMetrica, attributo: denMetrica === 'conteggio' ? null : denAttributo, filtri: [], attributo_data: denData || null, giorni_del_periodo: false }
          : null,
      raggruppa: raggruppa as CreateKpiInput['raggruppa'],
      sede: site?.id ?? null,
      anno_prima: false,
      grafico,
      unita: ratio ? (percent ? 'percentuale' : 'numero') : metrica !== 'conteggio' && value?.formato === 'importo' ? 'euro' : 'numero',
    };
  }, [type, metrica, attributo, data, grana, group, linkAttr, linkedType, linkedField, grafico, name, site, value, ratio, denType, denMetrica, denAttributo, denValue, denData, percent, groupClash]);

  // L'anteprima si ricalcola poco dopo l'ultima scelta.
  const preview = useMutation({ mutationFn: (c: CreateKpiInput) => api<KpiDto>(t(forView ? '/views/preview' : '/kpis/preview'), { body: c }) });
  const key = JSON.stringify(config);
  useEffect(() => {
    if (!config) return;
    const id = setTimeout(() => preview.mutate(config), 400);
    return () => clearTimeout(id);
  }, [key]);
  const save = useMutation({
    mutationFn: async () => {
      if (forView) {
        forView.onTile(config!.name, config!);
        return null;
      }
      return api<KpiDto>(t('/kpis'), { body: { ...config, visibilita, ruoli: visibilita === 'ruoli' ? ruoli : [] } });
    },
    onSuccess: (k) => {
      if (k) onSaved?.(k);
    },
  });

  return (
    <Card className="!p-5">
      <div className="mb-3 flex items-baseline gap-2">
        <h3 className="min-w-0 flex-1 text-sm font-semibold">{forView ? 'Nuovo riquadro' : 'Nuovo grafico'}{site ? ` per ${site.name}` : ''}</h3>
        <button type="button" className="text-xs text-ink/50 hover:text-ink" onClick={onCancel}>
          Annulla
        </button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Select label="Dati" value={tipo} onChange={(e) => setTipo(e.target.value)}>
          <option value="">scegli…</option>
          {types.map((x) => (
            <option key={x.chiave} value={x.chiave}>
              {x.etichetta}
            </option>
          ))}
        </Select>
        {type && (
          <div className="grid grid-cols-2 gap-2">
            <Select label="Cosa misurare" value={metrica} onChange={(e) => setMetrica(e.target.value as typeof metrica)}>
              <option value="conteggio">quanti sono</option>
              <option value="somma" disabled={!numeric.length}>
                somma di
              </option>
              <option value="media" disabled={!numeric.length}>
                media di
              </option>
            </Select>
            {metrica !== 'conteggio' && (
              <Select label="campo" value={attributo} onChange={(e) => setAttributo(e.target.value)}>
                <option value="" />
                {numeric.map((a) => (
                  <option key={a.nome} value={a.nome}>
                    {a.etichetta}
                  </option>
                ))}
              </Select>
            )}
          </div>
        )}
        {type && (
          <div className="sm:col-span-2">
            <label className="flex items-center gap-2 text-sm text-ink/70">
              <input type="checkbox" checked={ratio} onChange={(e) => setRatio(e.target.checked)} />
              Diviso per un’altra misura (un rapporto)
            </label>
            {ratio && (
              <div className="mt-2 grid gap-2 sm:grid-cols-4">
                <Select label="diviso per i dati" value={denTipo} onChange={(e) => setDenTipo(e.target.value)}>
                  <option value="">scegli…</option>
                  {types.map((x) => (
                    <option key={x.chiave} value={x.chiave}>
                      {x.etichetta}
                    </option>
                  ))}
                </Select>
                {denType && (
                  <Select label="cosa" value={denMetrica} onChange={(e) => setDenMetrica(e.target.value as typeof denMetrica)}>
                    <option value="conteggio">quanti sono</option>
                    <option value="somma" disabled={!denAttrs.some(isNumber)}>
                      somma di
                    </option>
                    <option value="media" disabled={!denAttrs.some(isNumber)}>
                      media di
                    </option>
                  </Select>
                )}
                {denType && denMetrica !== 'conteggio' && (
                  <Select label="campo" value={denAttributo} onChange={(e) => setDenAttributo(e.target.value)}>
                    <option value="" />
                    {denAttrs.filter(isNumber).map((a) => (
                      <option key={a.nome} value={a.nome}>
                        {a.etichetta}
                      </option>
                    ))}
                  </Select>
                )}
                {denType && data && (
                  <Select label="nello stesso periodo, per data di" value={denData} onChange={(e) => setDenData(e.target.value)}>
                    <option value="">uguale per ogni periodo</option>
                    {denAttrs.filter(isDate).map((a) => (
                      <option key={a.nome} value={a.nome}>
                        {a.etichetta}
                      </option>
                    ))}
                  </Select>
                )}
                <label className="flex items-center gap-2 text-sm text-ink/70 sm:col-span-4">
                  <input type="checkbox" checked={percent} onChange={(e) => setPercent(e.target.checked)} />
                  Mostra come percentuale
                </label>
                {groupClash && <p className="text-xs text-red-700 sm:col-span-4">Con un divisore di altri dati si divide solo per sede o per attività.</p>}
              </div>
            )}
          </div>
        )}
        {type && (
          <div className="grid grid-cols-2 gap-2">
            <Select label="Nel tempo, per data di" value={data} onChange={(e) => setData(e.target.value)} disabled={!dates.length}>
              <option value="">un solo totale</option>
              {dates.map((a) => (
                <option key={a.nome} value={a.nome}>
                  {a.etichetta}
                </option>
              ))}
            </Select>
            {data && (
              <Select label="per" value={grana} onChange={(e) => setGrana(e.target.value as typeof grana)}>
                <option value="mese">mese</option>
                <option value="anno">anno</option>
                <option value="giorno">giorno</option>
              </Select>
            )}
          </div>
        )}
        {type && (
          <div className="grid grid-cols-2 gap-2">
            <Select label="Diviso per" value={group} onChange={(e) => setGroup(e.target.value as GroupChoice)}>
              <option value="">nessuna divisione</option>
              {levels.multi && !site && <option value="sede">sede</option>}
              <option value="attivita">attività</option>
              {attrs.filter(isGroup).map((a) => (
                <option key={a.nome} value={`campo:${a.nome}`}>
                  {a.etichetta}
                </option>
              ))}
              {links.map((a) => (
                <option key={a.nome} value={`collegato:${a.nome}`}>
                  {a.etichetta} → un suo campo
                </option>
              ))}
            </Select>
            {linkedType && (
              <Select label={`campo di ${linkedType.etichetta.toLowerCase()}`} value={linkedField} onChange={(e) => setLinkedField(e.target.value)}>
                <option value="" />
                {linkedType.attributi.filter(isGroup).map((a) => (
                  <option key={a.nome} value={a.nome}>
                    {a.etichetta}
                  </option>
                ))}
              </Select>
            )}
          </div>
        )}
        {type && (
          <Select label="Grafico" value={grafico} onChange={(e) => setGrafico(e.target.value as typeof grafico)}>
            <option value="colonne" disabled={!data}>
              colonne nel tempo
            </option>
            <option value="linea" disabled={!data}>
              linea nel tempo
            </option>
            <option value="barre" disabled={!group}>
              barre per gruppo
            </option>
            <option value="numero">un numero solo</option>
          </Select>
        )}
        {type && <Field label="Nome" placeholder={config?.name ?? ''} value={name} onChange={(e) => setName(e.target.value)} />}
        {type && isManager && (
          <Select label="Chi lo vede" value={visibilita} onChange={(e) => setVisibilita(e.target.value as typeof visibilita)}>
            <option value="mia">solo io</option>
            <option value="tutti">tutti</option>
            <option value="ruoli">le persone di alcuni ruoli</option>
          </Select>
        )}
      </div>
      {visibilita === 'ruoli' && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {(roles.data?.data ?? []).map((r) => (
            <label key={r.key} className={`cursor-pointer rounded-md border px-2 py-1 text-xs ${ruoli.includes(r.key) ? 'border-accent bg-accent/10 text-accent' : 'border-black/10 text-ink/70'}`}>
              <input type="checkbox" className="sr-only" checked={ruoli.includes(r.key)} onChange={(e) => setRuoli((v) => (e.target.checked ? [...v, r.key] : v.filter((x) => x !== r.key)))} />
              {r.label}
            </label>
          ))}
        </div>
      )}
      {config && (
        <div className="mt-4 rounded-lg border border-black/[0.06] p-3">
          <p className="mb-2 text-xs font-medium text-ink/55">Anteprima{preview.isPending ? ' · calcolo…' : ''}</p>
          {preview.data?.manca?.length ? (
            <p className="text-sm text-ink/60">Non ci sono ancora dati da mostrare: servono voci di {preview.data.manca.map((m) => m.nome).join(' e ')}.</p>
          ) : preview.data?.serie.length ? (
            <KpiChart k={preview.data} />
          ) : preview.data ? (
            <p className="text-sm text-ink/60">Nessun valore.</p>
          ) : null}
          <ErrorText>{preview.error ? errorMessage(preview.error) : ''}</ErrorText>
        </div>
      )}
      <div className="mt-3 flex items-center gap-2">
        <Button disabled={!config || save.isPending || (visibilita === 'ruoli' && !ruoli.length)} onClick={() => save.mutate()}>
          {save.isPending ? 'Salvo…' : forView ? 'Aggiungi alla vista' : 'Aggiungi alla dashboard'}
        </Button>
        <ErrorText>{save.error ? errorMessage(save.error) : ''}</ErrorText>
      </div>
    </Card>
  );
}
